import { HolidayDateGlyph } from "./EGDayClosure";
import { dayClosure, openDayShifts } from "../../lib/egDayClosures";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ArrowLeftRight,
  CalendarDays,
  Users,
  AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { getShiftPresentation, EG_SHIFT_COLORS } from "@/lib/egShiftPresentation";
import {
  isoDay,
  overviewMonths,
  monthLabel,
  monthAttention,
  previewRoster,
  previewWeeks,
} from "@/lib/scheduleOverview";
import "./eg-schedule-overview.css";
import "./eg-overview-calendar.css";
const statusLabel = (p) =>
  p?.status
    ? String(p.status)
        .replace(/_/g, " ")
        .replace(/^./, (c) => c.toUpperCase())
    : "Not prepared";
function Pager({ page, setPage, total, size, label }) {
  const count = Math.ceil(total / size);
  return count > 1 ? (
    <nav className="eg-overview__pager" aria-label={label}>
      <button
        disabled={!page}
        onClick={() => setPage(page - 1)}
        aria-label={`Previous ${label}`}
      >
        <ChevronLeft size={15} />
      </button>
      <span>
        {page + 1} / {count}
      </span>
      <button
        disabled={page + 1 >= count}
        onClick={() => setPage(page + 1)}
        aria-label={`Next ${label}`}
      >
        <ChevronRight size={15} />
      </button>
    </nav>
  ) : null;
}
function OverviewShift({ shift }) {
  const display = getShiftPresentation(shift);
  return <span className={`eg-overview__shift${display.requested ? " is-requested" : ""}`} data-shift-type={display.type} data-mod={shift.mod === true || undefined}
    aria-label={`${display.label}${display.hasTimes ? ` ${shift.start}–${shift.end}` : ""}${shift.mod === true ? ", MOD" : ""}${display.requested ? ", Requested" : ""}`}>
    <i className="eg-overview__rail" style={{backgroundColor: EG_SHIFT_COLORS[display.type]}} aria-hidden="true" />
    {shift.mod === true && <i className="eg-overview__mod-rail" aria-hidden="true" />}
    <span className="eg-overview__shift-label">{display.label}</span>
    {display.hasTimes && <strong>{shift.start}–{shift.end}</strong>}
    {display.requested && <span className="eg-overview__requested">Requested</span>}
  </span>;
}
export default function EGScheduleOverview({
  boutique,
  periods,
  team,
  loading,
  periodError,
  onChooseMonth,
}) {
  const [data, setData] = useState({ loading: true }),
    [retry, setRetry] = useState(0),
    [attentionPage, setAttentionPage] = useState(0),
    [historyPage, setHistoryPage] = useState(0),
    [week, setWeek] = useState(0),
    [view, setView] = useState("week");
  const { current, upcoming, due } = overviewMonths();
  const currentPeriod = periods.find(
    (p) => p.period_start?.slice(0, 7) === current.start.slice(0, 7),
  );
  const upcomingPeriod = periods.find(
    (p) => p.period_start?.slice(0, 7) === upcoming.start.slice(0, 7),
  );
  const weeks = previewWeeks(current),
    today = isoDay(new Date());
  useEffect(() => {
    setAttentionPage(0);
    setHistoryPage(0);
    setWeek(
      Math.max(
        0,
        weeks.findIndex(
          (w) =>
            w <= today &&
            isoDay(
              new Date(new Date(`${w}T12:00:00`).getTime() + 6 * 86400000),
            ) >= today,
        ),
      ),
    );
  }, [boutique, current.start]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    setData({ loading: true });
    const endpoints = ["home", "time-off", "coverage", "swaps"];
    Promise.allSettled(
      endpoints.map((p) =>
        api.get(`/scheduling/${p}`, { params: { boutique } }),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next = { loading: false };
      results.forEach((r, i) => {
        const key = ["home", "timeOff", "coverage", "swaps"][i];
        if (r.status === "fulfilled")
          next[key] =
            i === 0
              ? r.value.data
              : r.value.data?.[["", "requests", "opportunities", "swaps"][i]] ||
                [];
        else next[`${key}Error`] = true;
      });
      setData(next);
    });
    return () => {
      cancelled = true;
    };
  }, [boutique, retry]);
  const [feedback, setFeedback] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setFeedback(null);
    if (upcomingPeriod?.status === "changes_requested")
      api
        .get(`/scheduling/periods/${upcomingPeriod.id}/change-log`)
        .then((r) => {
          if (!cancelled)
            setFeedback(
              (r.data?.entries || []).find(
                (e) =>
                  e.action === "changes_requested" ||
                  e.kind === "changes_requested",
              )?.detail ||
                "Changes requested. Open the schedule to review feedback.",
            );
        })
        .catch(() => {
          if (!cancelled) setFeedback("Reviewer feedback could not be loaded.");
        });
    return () => {
      cancelled = true;
    };
  }, [upcomingPeriod?.id, upcomingPeriod?.status]);
  const attention = useMemo(
    () => monthAttention(data, current),
    [data, current.start],
  ); // eslint-disable-line react-hooks/exhaustive-deps
  const nextAttention = useMemo(
    () => monthAttention(data, upcoming),
    [data, upcoming.start],
  ); // eslint-disable-line react-hooks/exhaustive-deps
  const roster = previewRoster(currentPeriod, team),
    shifts = openDayShifts(currentPeriod?.current_version),
    start = weeks[week] || weeks[0];
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(`${start}T12:00:00`);
    d.setDate(d.getDate() + i);
    return {
      date: isoDay(d),
      label: d.toLocaleDateString("en-US", { weekday: "short" }),
      number: d.getDate(),
      shortDate: d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
    };
  });
  const deadline = new Date(`${due}T12:00:00`),
    remaining = Math.ceil(
      (deadline - new Date(`${today}T12:00:00`)) / 86400000,
    );
  const requestErrors = ["timeOff", "coverage", "swaps"].filter(
    (k) => data[`${k}Error`],
  );
  const upcomingOpen = upcomingPeriod ? (
    <Link
      className="eg-overview__button eg-overview__button--primary"
      to={`/scheduling/eg/${upcomingPeriod.id}`}
    >
      {upcomingPeriod.can_edit ? "Continue" : "Open"}{" "}
      {monthLabel(upcoming.start).split(" ")[0]}{" "}
      {upcomingPeriod.can_edit ? "draft" : "schedule"}
      <ArrowRight size={16} />
    </Link>
  ) : data.home?.can_manage ? (
    <button
      className="eg-overview__button eg-overview__button--primary"
      onClick={onChooseMonth}
    >
      Prepare {monthLabel(upcoming.start).split(" ")[0]} schedule
      <ArrowRight size={16} />
    </button>
  ) : (
    <p className="eg-overview__quiet">
      Your manager hasn’t prepared this month yet.
    </p>
  );
  return (
    <div className="eg-overview" data-testid="eg-overview">
      <div className="eg-overview__bento">
        <div className="eg-overview__left">
          <section
            className="eg-overview__card eg-overview__current"
            aria-label="Current month schedule"
          >
            <div className="eg-overview__current-heading">
              <div className="eg-overview__eyebrow">
                Current schedule{" "}
                <span className="eg-overview__status">
                  {loading
                    ? "Loading…"
                    : periodError
                      ? "Unavailable"
                      : statusLabel(currentPeriod)}
                </span>
              </div>
              <div className="eg-overview__title-row">
                <h2>{monthLabel(current.start)}</h2>
                {currentPeriod && (
                  <Link
                    className="eg-overview__button"
                    to={`/scheduling/eg/${currentPeriod.id}`}
                  >
                    Open current schedule
                    <ArrowRight size={16} />
                  </Link>
                )}
                {currentPeriod && !loading && !periodError && (
                  <Link
                    className="eg-overview__button"
                    to={`/scheduling/time-off?boutique=${boutique}`}
                  >
                    Request a change
                  </Link>
                )}
              </div>
            </div>
            {loading ? (
              <p role="status">Loading the current schedule…</p>
            ) : periodError ? (
              <p role="alert">
                The schedule could not be loaded. Please refresh to try again.
              </p>
            ) : !currentPeriod ? (
              <p className="eg-overview__empty">
                No schedule has been prepared for this month.
              </p>
            ) : (
              <>
                <div className="eg-overview__week-bar">
                  <nav className="eg-overview__week-pager" aria-label="Preview week">
                    <button onClick={() => setWeek(week - 1)} disabled={!week} aria-label="Previous preview week"><ChevronLeft size={16} /></button>
                    <span>Week of {new Date(`${start}T12:00:00`).toLocaleDateString("en-US", {month:"long",day:"numeric"})}–{days[6].date.slice(0,7) === start.slice(0,7) ? days[6].number : days[6].shortDate}</span>
                    <button onClick={() => setWeek(week + 1)} disabled={week >= weeks.length - 1} aria-label="Next preview week"><ChevronRight size={16} /></button>
                  </nav>
                  <button className="eg-overview__view-toggle" onClick={() => setView(view === "week" ? "month" : "week")} aria-expanded={view === "month"}>{view === "week" ? "Week" : "Month"}</button>
                </div>
                {view === "month" && (
                  <div
                    className="eg-overview__weeks"
                    aria-label="Choose preview week"
                  >
                    {weeks.map((w, i) => (
                      <button
                        key={w}
                        aria-pressed={i === week}
                        onClick={() => {
                          setWeek(i);
                          setView("week");
                        }}
                      >
                        Week of{" "}
                        {new Date(`${w}T12:00:00`).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </button>
                    ))}
                  </div>
                )}
                {roster.length ? (
                  <div
                    className="eg-overview__table-scroll"
                    tabIndex="0"
                    aria-label="Complete weekly roster; scroll for additional employees or days"
                  >
                    <table style={{"--eg-overview-rows": roster.length, "--eg-overview-row-tracks": roster.map(person=>`${Math.max(1,...days.map(day=>shifts.filter(shift=>String(shift.employee_id)===String(person.employee_id) && shift.date===day.date && shift.date>=current.start && shift.date<=current.end).length))}fr`).join(" ")}}>
                      <thead>
                        <tr>
                          <th scope="col">Experience Guide</th>
                          {days.map((d) => (
                            <th
                              key={d.date}
                              scope="col"
                              className={dayClosure(currentPeriod?.current_version,d.date) ? "is-holiday" : d.date === today ? "is-today" : ""}
                            >
                              {dayClosure(currentPeriod?.current_version,d.date)?<span><HolidayDateGlyph glyph={dayClosure(currentPeriod.current_version,d.date).glyph}/>{d.label} {d.number}</span>:d.label}
                              <small>
                                {dayClosure(currentPeriod?.current_version,d.date)?"Holiday":d.number}
                                {!dayClosure(currentPeriod?.current_version,d.date)&&d.date === today && <em>Today</em>}
                              </small>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {roster.map((r) => (
                            <tr key={r.employee_id}>
                              <th scope="row">
                                {r.employee_name ||
                                  r.name ||
                                  "Name unavailable"}
                              </th>
                              {days.map((d) => {
                                const entries = shifts.filter(
                                  (s) =>
                                    String(s.employee_id) ===
                                      String(r.employee_id) &&
                                    s.date === d.date &&
                                    s.date >= current.start &&
                                    s.date <= current.end,
                                );
                                return (
                                  <td
                                    key={d.date}
                                    className={
                                      dayClosure(currentPeriod?.current_version,d.date) ? "is-holiday" : d.date === today ? "is-today" : ""
                                    }
                                  >
                                    {dayClosure(currentPeriod?.current_version,d.date) ? <span aria-label="Holiday closed"/> : entries.length ? (
                                      entries.map((s, i) => (
                                        <OverviewShift key={s.id || i} shift={s} />
                                      ))
                                    ) : (
                                      <span
                                        aria-label={
                                          d.date < current.start ||
                                          d.date > current.end
                                            ? "Outside current month"
                                            : "No saved shift"
                                        }
                                      >
                                        —
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="eg-overview__quiet">
                    No roster is available for this schedule.
                  </p>
                )}
                <div className="eg-overview__preview-footer">
                  <span>— No saved shift · read-only preview</span><span className="eg-overview__mod-key"><i /> MOD · independent of shift category</span>
                </div>
              </>
            )}
          </section>
          <section
            className={`eg-overview__card eg-overview__attention${!data.loading && !requestErrors.length && !attention.length ? " is-empty" : ""}`}
            aria-label="Changes affecting the current month"
          >
            <div className="eg-overview__eyebrow">
              <AlertCircle size={15} />
              Impacting {monthLabel(current.start).split(" ")[0]}
            </div>
            <div className="eg-overview__title-row">
              <h3>Changes to review</h3>
              {!data.loading && !requestErrors.length && (
                <span>{attention.length} pending</span>
              )}
              <Pager
                page={attentionPage}
                setPage={setAttentionPage}
                total={attention.length}
                size={2}
                label="request pages"
              />
            </div>
            {data.loading ? (
              <p role="status">Loading requests…</p>
            ) : (
              <>
                {requestErrors.length > 0 && (
                  <p role="alert">
                    Some requests could not be loaded. Counts may be incomplete.{" "}
                    <button onClick={() => setRetry((n) => n + 1)}>
                      Try again
                    </button>
                  </p>
                )}
                {!attention.length && !requestErrors.length && (
                  <p className="eg-overview__quiet">
                    No pending changes for this month.
                  </p>
                )}
                {attention
                  .slice(attentionPage * 2, attentionPage * 2 + 2)
                  .map((item) => {
                    const Icon =
                      item.kind === "swaps"
                        ? ArrowLeftRight
                        : item.kind === "coverage"
                          ? Users
                          : CalendarDays;
                    return (
                      <div className="eg-overview__request" key={item.id}>
                        <Icon size={20} />
                        <div>
                          <strong>
                            {item.title} ·{" "}
                            {new Date(
                              `${item.date}T12:00:00`,
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                            {item.end && item.end !== item.date
                              ? `–${new Date(`${item.end}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                              : ""}
                          </strong>
                          <span>
                            {item.name} · {item.detail}
                          </span>
                        </div>
                        <Link className="eg-overview__button" to={item.to}>
                          {item.action}
                        </Link>
                      </div>
                    );
                  })}
              </>
            )}
          </section>
        </div>
        <section
          className="eg-overview__card eg-overview__upcoming"
          aria-label="Upcoming month schedule"
        >
          <div className="eg-overview__eyebrow">
            Upcoming schedule{" "}
            <span className="eg-overview__status">
              {loading
                ? "Loading…"
                : periodError
                  ? "Unavailable"
                  : statusLabel(upcomingPeriod)}
            </span>
          </div>
          <h2>{monthLabel(upcoming.start)}</h2>
          <div className="eg-overview__deadline">
            <strong>
              Submit to Katrina by{" "}
              {deadline.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
              })}
            </strong>
            <span>
              {["submitted", "approved", "published"].includes(
                upcomingPeriod?.status,
              )
                ? upcomingPeriod.deadline?.text || statusLabel(upcomingPeriod)
                : remaining < 0
                  ? `${Math.abs(remaining)} days past the submission deadline`
                  : remaining === 0
                    ? "Submission deadline is today"
                    : `${remaining} days remaining`}
            </span>
          </div>
          <div className="eg-overview__upcoming-row">
            <div>
              <strong>Availability</strong>
              <span>
                Standing preferences · monthly response tracking isn’t
                configured.
              </span>
            </div>
            <Link
              to={`/scheduling/preferences?boutique=${boutique}`}
              className="eg-overview__button"
            >
              Review
            </Link>
          </div>
          {["timeOff", "coverage", "swaps"].map((kind) => (
            <div className="eg-overview__upcoming-row" key={kind}>
              <div>
                <strong>
                  {
                    {
                      timeOff: "Time-off requests",
                      coverage: "Coverage",
                      swaps: "Shift swaps",
                    }[kind]
                  }
                </strong>
                <span>
                  {data.loading
                    ? "Loading…"
                    : data[`${kind}Error`]
                      ? "Could not load requests"
                      : `${nextAttention.filter((i) => i.kind === kind).length} pending for ${monthLabel(upcoming.start).split(" ")[0]}`}
                </span>
              </div>
              <Link
                className="eg-overview__button"
                to={`${{ timeOff: "/scheduling/time-off", coverage: "/scheduling/coverage", swaps: "/scheduling/swaps" }[kind]}?boutique=${boutique}&view=all`}
              >
                Review
              </Link>
            </div>
          ))}
          {feedback && <p className="eg-overview__feedback">{feedback}</p>}
          <div className="eg-overview__upcoming-actions">
            {!loading && !periodError && upcomingOpen}
            {periodError && (
              <p role="alert">Upcoming schedule could not be loaded.</p>
            )}
            {data.homeError && (
              <p className="eg-overview__quiet">
                Schedule permissions could not be loaded.
              </p>
            )}
            {data.home?.can_manage && (
              <button className="eg-overview__other" onClick={onChooseMonth}>
                Other months
              </button>
            )}
          </div>
        </section>
      </div>
      {!!periods.length && (
        <details className="eg-overview__history">
          <summary>Monthly schedule history</summary>
          <ul data-testid="eg-period-list">
            {periods.slice(historyPage * 4, historyPage * 4 + 4).map((p) => (
              <li key={p.id} data-testid={`eg-period-${p.id}`}>
                <span>
                  {p.label || monthLabel(p.period_start)}{" "}
                  <small>{statusLabel(p)}</small>
                </span>
                <Link
                  className="eg-overview__button"
                  data-testid={`eg-open-${p.id}`}
                  to={`/scheduling/eg/${p.id}`}
                >
                  Open
                  <ArrowRight size={14} />
                </Link>
              </li>
            ))}
          </ul>
          <Pager
            page={historyPage}
            setPage={setHistoryPage}
            total={periods.length}
            size={4}
            label="history pages"
          />
        </details>
      )}
    </div>
  );
}
